import React, { useState } from 'react'
import { Skeleton, message } from 'antd'

import { Item } from "../../Item"
import CopyString from '../../CopyString'
import { Tags } from '../Tags'
import { RxChangeRequestModal } from '../RxChangeRequestModal'
import CaseReviewRejectionModal from '../CaseReviewRejectionModal'
import UpdateCaseStatusModal from '../UpdateCaseStatusModal'
import ReassignModal from '../ReassignModal'
import { ActionButtons } from '../ActionButtons'

import { useCaseViewContext } from '../../../context'
import { useCaseTags } from '../../../hooks'
import { REQUEST_STATUS } from '../../../constants'

import {
    Container,
    Header,
} from "../../../Styles"

import { useReferrals } from '../../../hooks'
import { dateTimeFormatter } from "../../../utils"

import {
    HeaderContainer,
    SubHeader,
    StatusContainer,
    StatusLabel,
    CaseStatusLabel,
    CevFeedbackWrapper,
    CevFeedbackLabel,
} from './styles'
import { StatusHistoryPopover } from './StatusHistoryPopover'

export const Metadata = () => {
    const { 
        caseDetails, 
        activeRequest, 
        fetchCaseDetails,
        apiClasses,
        allowTagEditing,
        apiType,
    } = useCaseViewContext()

    const {
        approveReview,
        rejectReview,
        showReviewModal,
        hideReviewModal,
        reviewModalVisible,
    } = useReferrals()

    const [showRxChangeModal, setShowRxChangeModal] = useState(false)
    const [showReassignModal, setShowReassignModal] = useState(false)
    const [showUpdateCaseStatusModal, setShowUpdateCaseStatusModal] = useState(false)

    const { availableTags, handleTagChange } = useCaseTags({
        caseId: caseDetails?._id || caseDetails?.caseId,
        apiClasses,
    })

    const displayableRequestStatus = {
        [REQUEST_STATUS.COMPLETED]: 'Completed, Prescribed',
        [REQUEST_STATUS.COMPLETED_NO_RX]: 'Completed, Not Prescribed',
        [REQUEST_STATUS.CANCELED]: 'Cancelled',
    }

    const requestStatus = activeRequest?.status
    const requestIsCompleted = Object.keys(displayableRequestStatus).includes(requestStatus)
    const requestIsCanceled = requestStatus === REQUEST_STATUS.CANCELED
    const requestTimestamp = requestIsCompleted ? activeRequest?.completedAt : activeRequest?.createdAt
    const consultationType = activeRequest?.requestType
    const providerData = caseDetails?.provider || caseDetails?.assignedProvider || caseDetails?.physician
    const physicianName = caseDetails?.physicianName
    const correctedRequestStatusString = requestIsCanceled ? 'Cancelled' : requestStatus.toLowerCase()

    const providerName = providerData
        ? `${providerData?.firstName} ${providerData?.lastName}`
        : physicianName || 'Unassigned'

    return (
        <Container>
            <Skeleton loading={!consultationType || !caseDetails} active>
                <HeaderContainer>
                    <Header>{`${consultationType} Consultation`}</Header>
                </HeaderContainer>
                <SubHeader>{caseDetails?.categoryName || caseDetails?.category?.name}</SubHeader>
                <div>{dateTimeFormatter(requestTimestamp)}</div>
                <ActionButtons 
                    requestIsCompleted={requestIsCompleted}
                    approveReview={approveReview}
                    showReviewModal={showReviewModal}
                    setShowRxChangeModal={setShowRxChangeModal}
                    setShowReassignModal={setShowReassignModal}
                    setShowUpdateCaseStatusModal={setShowUpdateCaseStatusModal}
                />
                <Item 
                    label="Request Status" 
                    value={
                        <StatusContainer>
                            <StatusLabel>{correctedRequestStatusString}</StatusLabel>
                        </StatusContainer>
                    }
                />
                {
                    caseDetails?.status && <Item
                        label="Case Status"
                        value={
                            <StatusContainer>
                                <CaseStatusLabel>{caseDetails.status}</CaseStatusLabel>
                                <StatusHistoryPopover statusHistory={caseDetails.statusHistory} />
                            </StatusContainer>
                        }
                    />
                }
                {
                    activeRequest?.reason && (
                        <Item label="Rx Change Reason" value={activeRequest.reason} />
                    )
                }
                <Item label="Case ID" value={ <CopyString text={caseDetails?._id || caseDetails?.caseId} />} />
                <Item label="Consultation Type" value={`${caseDetails?.consultationType}`} />
                <Item label="Provider" value={providerName} />
                {
                    activeRequest?.oneClickPrescribeAssessment?.overrideFeedback && (
                        <CevFeedbackWrapper>
                            <Item 
                                label={<CevFeedbackLabel>CEV Override Feedback</CevFeedbackLabel>} 
                                value={activeRequest.oneClickPrescribeAssessment.overrideFeedback}
                            />
                        </CevFeedbackWrapper>
                    )
                }
                <Item 
                    label="Tags" 
                    value={
                        <Tags
                            currentTags={caseDetails?.tags || []}
                            caseId={caseDetails?._id}
                            onTagsChange={handleTagChange}
                            readOnlyMode={!allowTagEditing}
                            availableTags={availableTags}
                        />
                    } 
                />
            </Skeleton>
            
            <RxChangeRequestModal
                visible={showRxChangeModal}
                onCancel={() => setShowRxChangeModal(false)}
                caseId={caseDetails?._id || caseDetails?.caseId}
                caseDetails={caseDetails}
                onSuccess={() => {
                    if (fetchCaseDetails) {
                        fetchCaseDetails()
                    }
                }}
            />
            <CaseReviewRejectionModal 
                rejectReview={rejectReview}
                reviewModalVisible={reviewModalVisible}
                showReviewModal={showReviewModal}
                hideReviewModal={hideReviewModal}
            />
            <ReassignModal
                apiClasses={apiClasses}
                caseId={caseDetails?._id || caseDetails?.caseId}
                visible={showReassignModal}
                setVisible={setShowReassignModal}
                onSuccess={() => {
                    if (fetchCaseDetails) {
                        message.success("Case reassigned successfully")
                        setShowReassignModal(false)
                        fetchCaseDetails()
                    }
                }}
            />
            <UpdateCaseStatusModal
                apiClasses={apiClasses}
                apiType={apiType}
                caseId={caseDetails?._id || caseDetails?.caseId}
                visible={showUpdateCaseStatusModal}
                setVisible={setShowUpdateCaseStatusModal}
                onSuccess={() => {
                    if (fetchCaseDetails) {
                        message.success("Case status updated successfully")
                        setShowUpdateCaseStatusModal(false)
                        fetchCaseDetails()
                    }
                }}
            />
        </Container>
    )
}
